/**
 * HeroCTA - Prominent call-to-action for gallery review workflow.
 *
 * Displays a large, visually prominent button to start the review process.
 * Shows pending count as primary motivation driver.
 *
 * Design:
 * - Large format for primary action emphasis
 * - Animated entrance with Framer Motion
 * - Pending count as badge
 * - Keyboard shortcut hint (Enter)
 *
 * @see /src/features/gallery/pages/GalleryOverviewPage.tsx - Primary usage
 */

import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Play, CheckCircle, ImageIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

export interface HeroCTAProps {
  /** Link destination */
  href: string
  /** Number of images pending review */
  pendingCount: number
  /** Total images in gallery */
  totalCount: number
  /** Optional class name */
  className?: string
}

/**
 * HeroCTA - Primary action button for starting image review.
 */
export function HeroCTA({
  href,
  pendingCount,
  totalCount,
  className,
}: HeroCTAProps) {
  const allReviewed = pendingCount === 0

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={cn('w-full', className)}
    >
      <Link to={href} className="block">
        <motion.div
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          className={cn(
            'relative overflow-hidden rounded-xl p-5 sm:p-8',
            'border-2 transition-colors',
            allReviewed
              ? 'bg-success/5 border-success/30 hover:border-success/50'
              : 'bg-primary/5 border-primary/30 hover:border-primary/50'
          )}
        >
          {/* Decorative background pattern */}
          <div
            className={cn(
              'absolute inset-0 opacity-5',
              allReviewed ? 'bg-success' : 'bg-primary'
            )}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />

          <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-6">
            {/* Left: Icon + Text */}
            <div className="flex items-center gap-4 sm:gap-5">
              <div
                className={cn(
                  'p-3 sm:p-4 rounded-xl shrink-0',
                  allReviewed
                    ? 'bg-success/10'
                    : 'bg-primary/10'
                )}
              >
                {allReviewed ? (
                  <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8 text-success" />
                ) : (
                  <ImageIcon className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
                )}
              </div>

              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-0.5 sm:mb-1">
                  {allReviewed ? 'All Images Reviewed' : 'Review Images'}
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground">
                  {allReviewed
                    ? `${totalCount} images approved and ready for use`
                    : `${pendingCount} of ${totalCount} images need your attention`}
                </p>
              </div>
            </div>

            {/* Right: Action */}
            <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 mt-2 sm:mt-0">
              {!allReviewed && (
                <Badge variant="warning" className="text-sm sm:text-base px-2 sm:px-3 py-0.5 sm:py-1">
                  {pendingCount} pending
                </Badge>
              )}

              <Button
                size="lg"
                variant={allReviewed ? 'secondary' : 'default'}
                className="gap-2 flex-1 sm:flex-none sm:min-w-[140px] h-10 sm:h-11"
              >
                {allReviewed ? (
                  <>
                    View Gallery
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 sm:h-5 sm:w-5" />
                    Start Review
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Keyboard hint */}
          {!allReviewed && (
            <div className="absolute bottom-3 right-4 text-xs text-muted-foreground">
              Press{' '}
              <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">
                Enter
              </kbd>{' '}
              to start
            </div>
          )}
        </motion.div>
      </Link>
    </motion.div>
  )
}

export default HeroCTA
